import {
  TrendingUp,
  TrendingDown,
  MapPin,
  DollarSign,
  AlertCircle,
  UserCheck,
  RefreshCw,
  GraduationCap,
  FileCheck,
  Repeat,
  ClipboardList,
} from "lucide-react";

// ─── Action Type Definitions ─────────────────────────────────────────────────
// Each entry is consumed by ActionModal, ConfirmModal, HistoryModal,
// PendingApprovalsModal, EmployeeTable, and the main EmployeeActionsPage.
//
// Shape:
//   id               – stored as `actionType` in the DB
//   labelKey         – key into translations (e.g. "promoteEmployee")
//   descriptionKey   – key into translations for the short description
//   icon             – Lucide icon component
//   color            – gradient string used for buttons / headers
//   bgColor          – translucent background for badges
//   borderColor      – border for badges
//   requiresApproval – whether the action enters the approval workflow
//   approvalLevelKey – fallback translation key (used when no workflow definition is loaded)

export const ACTION_TYPES = [
  {
    id: "promote",
    labelKey: "promoteEmployee",
    descriptionKey: "advanceToHigher",
    icon: TrendingUp,
    color: "linear-gradient(135deg, #10b981, #059669)",
    bgColor: "rgba(16, 185, 129, 0.15)",
    borderColor: "rgba(16, 185, 129, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "demote",
    labelKey: "demoteEmployee",
    descriptionKey: "moveToLower",
    icon: TrendingDown,
    color: "linear-gradient(135deg, #f59e0b, #d97706)",
    bgColor: "rgba(245, 158, 11, 0.15)",
    borderColor: "rgba(245, 158, 11, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "transfer",
    labelKey: "transferEmployee",
    descriptionKey: "transferDifferent",
    icon: MapPin,
    color: "linear-gradient(135deg, #3b82f6, #2563eb)",
    bgColor: "rgba(59, 130, 246, 0.15)",
    borderColor: "rgba(59, 130, 246, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "salary_change",
    labelKey: "salaryAdjustment",
    descriptionKey: "modifyCompensation",
    icon: DollarSign,
    color: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
    bgColor: "rgba(139, 92, 246, 0.15)",
    borderColor: "rgba(139, 92, 246, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "terminate",
    labelKey: "terminateEmployment",
    descriptionKey: "endEmployment",
    icon: AlertCircle,
    color: "linear-gradient(135deg, #ef4444, #dc2626)",
    bgColor: "rgba(239, 68, 68, 0.15)",
    borderColor: "rgba(239, 68, 68, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "suspend",
    labelKey: "suspendEmployee",
    descriptionKey: "temporarySuspension",
    icon: AlertCircle,
    color: "linear-gradient(135deg, #f97316, #ea580c)",
    bgColor: "rgba(249, 115, 22, 0.15)",
    borderColor: "rgba(249, 115, 22, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "rehire",
    labelKey: "rehireEmployee",
    descriptionKey: "reinstateFormer",
    icon: UserCheck,
    color: "linear-gradient(135deg, #06b6d4, #0891b2)",
    bgColor: "rgba(6, 182, 212, 0.15)",
    borderColor: "rgba(6, 182, 212, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  /*
  {
    id: "contract_update",
    labelKey: "contractUpdate",
    descriptionKey: "modifyContract",
    icon: FileCheck,
    color: "linear-gradient(135deg, #6366f1, #4f46e5)",
    bgColor: "rgba(99, 102, 241, 0.15)",
    borderColor: "rgba(99, 102, 241, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "rotation",
    labelKey: "jobRotation",
    descriptionKey: "rotateDifferent",
    icon: Repeat,
    color: "linear-gradient(135deg, #14b8a6, #0d9488)",
    bgColor: "rgba(20, 184, 166, 0.15)",
    borderColor: "rgba(20, 184, 166, 0.4)",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  }
    */
];

// ─── Action ID → Workflow subType map ────────────────────────────────────────
// Used by getWorkflowApprovalLabel to look up the correct WorkflowDefinition.
// Values must match WorkflowsTab ACTION_SUBTYPE_OPTIONS.

export const ACTION_SUBTYPE_MAP = {
  promote: "PROMOTION",
  demote: "DEMOTION",
  transfer: "TRANSFER",
  salary_change: "SALARY_ADJUSTMENT",
  terminate: "TERMINATION_EMPLOYER",
  suspend: "SUSPENSION",
  rehire: "REHIRE",
  contract_update: "CONTRACT",
  rotation: "ROTATION",
};

// ─── Human-readable assignee type labels ─────────────────────────────────────
const ASSIGNEE_LABELS = {
  DIRECT_MANAGER: { en: "Direct Manager", ar: "مدير مباشر" },
  SECOND_MANAGER: { en: "Second Manager", ar: "مدير ثانٍ" },
  DEPARTMENT_MANAGER: { en: "Dept. Manager", ar: "مدير القسم" },
  ADMIN_LEVEL: { en: "Admin Level", ar: "مستوى إداري" },
  EXPLICIT: { en: "Specific Assignee", ar: "موظف محدد" },
};

/**
 * Derives a human-readable approval-chain label from loaded workflow definitions
 * for a given action ID.
 *
 * @param {string}   actionId           – e.g. "promote"
 * @param {Array}    workflowDefinitions – array returned by listWorkflowDefinitions()
 * @param {string}   language            – "ar" | "en"
 * @param {object}   [t]                – translations object (used for the fallback key)
 * @param {string}   [fallbackKey]      – translation key to use when no definition found
 * @returns {string}
 */
export function getWorkflowApprovalLabel(
  actionId,
  workflowDefinitions,
  language,
  t,
  fallbackKey
) {
  const subType = ACTION_SUBTYPE_MAP[actionId];

  const definition =
    subType &&
    Array.isArray(workflowDefinitions) &&
    workflowDefinitions.find(
      (def) =>
        def.module === "ACTION" &&
        def.subType === subType &&
        // prefer company-wide (no targetEmployeeType) but accept any
        (!def.targetEmployeeType || def.targetEmployeeType === "__default__")
    );

  // Fall back to first matching subType regardless of targetEmployeeType
  const resolvedDef =
    definition ||
    (subType &&
      Array.isArray(workflowDefinitions) &&
      workflowDefinitions.find(
        (def) => def.module === "ACTION" && def.subType === subType
      ));

  if (resolvedDef && Array.isArray(resolvedDef.steps) && resolvedDef.steps.length > 0) {
    const lang = language === "ar" ? "ar" : "en";

    // Build "Step 1 (Direct Manager) → Step 2 (Admin Level)" style label
    const parts = resolvedDef.steps
      .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
      .map((step) => {
        const assigneeLabel =
          ASSIGNEE_LABELS[step.assigneeType]?.[lang] || step.assigneeType || "";
        const stepName = step.stepName?.trim();
        return stepName ? `${stepName} (${assigneeLabel})` : assigneeLabel;
      });

    return parts.join(language === "ar" ? " ← " : " → ");
  }

  // Fallback: use the static translation key
  if (t && fallbackKey) return t[fallbackKey] || fallbackKey;
  return "";
}

// ─── Action → Feature Permission Map ────────────────────────────────────────
// Maps each action id to the feature key checked in
// `permissions.features.changesPage` for admin-level gating.
// Keys MUST match the backend permission registry
// (user-service/src/config/permissionRegistry.js → page "changesPage").

export const ACTION_FEATURE_MAP = {
  promote: "promoteemployee",
  demote: "demoteemployee",
  transfer: "transferemployee",
  salary_change: "salaryadjustment",
  terminate: "terminateemployment",
  suspend: "suspendemployee",
  rehire: "rehireemployee",
  contract_update: "contractupdate",
  rotation: "jobrotation",
};
